import { useState, useEffect, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUsersApi } from "@/hooks/useUsersApi";
import LoadingSpinner from "@/components/orders/LoadingSpinner";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: "admin" | "manager";
  fallback?: ReactNode;
}

// Cache para evitar múltiples llamadas a la API
const permissionsCache = new Map<
  string,
  { userType: string; timestamp: number }
>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * ProtectedRoute - Componente para proteger rutas basado en roles de usuario
 *
 * @example
 * // Proteger una ruta solo para administradores
 * <ProtectedRoute requiredRole="admin">
 *   <AdminPanel />
 * </ProtectedRoute>
 *
 * // Proteger una ruta para managers con fallback personalizado
 * <ProtectedRoute
 *   requiredRole="manager"
 *   fallback={<CustomUnauthorizedMessage />}
 * >
 *   <ManagerDashboard />
 * </ProtectedRoute>
 */
export default function ProtectedRoute({
  children,
  requiredRole = "admin",
  fallback,
}: ProtectedRouteProps) {
  const { user } = useAuth();
  const { getUserByEmail } = useUsersApi();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      if (!user?.email) {
        setIsAuthorized(false);
        setIsLoading(false);
        return;
      }

      try {
        // Verificar caché primero
        const cached = permissionsCache.get(user.email);
        const now = Date.now();

        if (cached && now - cached.timestamp < CACHE_DURATION) {
          const hasPermission = cached.userType === requiredRole;
          setIsAuthorized(hasPermission);
          setIsLoading(false);
          return;
        }

        // Hacer llamada a la API si no hay caché válido
        const userData = await getUserByEmail(user.email);
        const userType = userData?.userType;

        // Guardar en caché
        if (userType) {
          permissionsCache.set(user.email, {
            userType,
            timestamp: now,
          });
        }

        // Verificar permisos
        const hasPermission = userType === requiredRole;
        setIsAuthorized(hasPermission);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermissions();
  }, [user?.email, requiredRole, getUserByEmail]);

  // Mostrar spinner mientras verifica permisos
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Mostrar fallback o mensaje por defecto si no está autorizado
  if (!isAuthorized) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-medium text-red-600 mb-2">
            No tienes acceso para ver esta información
          </p>
          <p className="text-sm text-gray-500">
            Solo los{" "}
            {requiredRole === "admin" ? "administradores" : "encargados"} pueden
            acceder a esta sección.
          </p>
        </div>
      </div>
    );
  }

  // Si está autorizado, renderizar el contenido
  return <>{children}</>;
}
